<template>
  <div class="duplicate-form">
    <header class="form-header">
      <div class="form-eyebrow">Duplicate Version</div>
      <h2 class="form-title">{{ sourceVersionLabel }}</h2>
      <p class="form-hint">
        Start a new version by reusing this composition. Pick a different scan to be the new
        primary if you want, drop scans you don't want to carry over, and add new ones. Transforms
        and trim radii of kept scans are rebased onto the new primary's frame so they keep their
        world positions; new scans start at identity and can be placed in the alignment editor.
      </p>
    </header>

    <v-row class="mb-2">
      <v-col cols="12" md="6">
        <v-text-field v-model="versionName" label="Version Name" variant="outlined" density="comfortable" hide-details />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field v-model="versionDescription" label="Version Description" variant="outlined" density="comfortable" hide-details />
      </v-col>
    </v-row>

    <section class="picker">
      <div class="section-eyebrow">Carry-over scans</div>
      <p class="section-hint">Untick a row to leave that scan out of the new version. Pick the radio button
        to make a scan the new primary (its current transform becomes the new origin).</p>

      <div class="scan-rows">
        <div v-for="scan in sourceScans" :key="scan.scanId" class="scan-row"
          :class="{ 'is-primary': scan.scanId === newPrimaryScanId, 'is-dropped': !keepScanIds.includes(scan.scanId) }">

          <div class="scan-radio">
            <input type="radio" :id="`primary-${scan.scanId}`" :value="scan.scanId"
              v-model="newPrimaryScanId" :disabled="!keepScanIds.includes(scan.scanId)"
              :title="keepScanIds.includes(scan.scanId) ? 'Make primary' : 'Drop is selected — re-tick to set as primary'" />
            <label :for="`primary-${scan.scanId}`" class="visually-hidden">Make primary</label>
          </div>

          <div class="scan-body">
            <span class="scan-name">{{ scan.name }}</span>
            <span v-if="scan.scanId === sourcePrimaryScanId" class="scan-badge was">was primary</span>
            <span v-if="scan.scanId === newPrimaryScanId" class="scan-badge new">new primary</span>
            <span v-if="(scan.trimRadius || 0) > 0" class="scan-meta">trim {{ scan.trimRadius.toFixed(2) }} m</span>
          </div>

          <button type="button" class="scan-toggle" :title="keepScanIds.includes(scan.scanId) ? 'Drop from new version' : 'Carry over to new version'"
            @click="toggleKeep(scan.scanId)">
            <v-icon size="16">
              {{ keepScanIds.includes(scan.scanId) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
            </v-icon>
          </button>
        </div>
      </div>
    </section>

    <section class="picker">
      <div class="section-eyebrow">Add new scans</div>
      <p class="section-hint">Added scans come in as supplementaries at identity — position them in
        the alignment editor afterwards. The primary stays one of the carry-over scans since those
        have transforms we can rebase against.</p>
      <v-select v-if="addableScans.length" v-model="addScanIds" :items="addableScans" item-title="name" item-value="id"
        label="Pick additional scans" multiple chips variant="outlined" density="comfortable" hide-details
        class="add-select" />
      <div v-else class="empty-note">No other processed scans are available on this asset.</div>
    </section>

    <v-checkbox v-model="setToCurrent" label="Set new version as current" color="mint" />

    <v-alert v-if="errorMessage" variant="tonal" density="compact" type="error" class="mt-2">
      {{ errorMessage }}
    </v-alert>

    <div class="actions">
      <v-btn class="cancel-btn" variant="text" @click="$emit('cancel')">Cancel</v-btn>
      <LoadingButton :disabled="!canSubmit" :loading="submitting" customClass="mint-btn" buttonText="Create version"
        @click="onCreate" />
    </div>
  </div>
</template>

<script>
import { useAuth0 } from '@auth0/auth0-vue';
import LoadingButton from '@/components/buttons/LoadingButton.vue';
import CreateModelVersionModel from '@/domain/modelversion/CreateModelVersionModel';
import ModelVersionService from '@/services/ModelVersionService';
import { SCAN_PROCESSING_SUCCESS } from '@/utils/ScanStatus';
import { rebaseCompositionOnNewPrimary, identityTransform } from '@/lib/modelversion/rebaseToNewPrimary';

export default {
  name: 'DuplicateVersionDialog',
  components: { LoadingButton },
  props: {
    sourceVersion: { type: Object, required: true },
    assetId: { type: String, required: true },
  },
  emits: ['created', 'cancel'],
  setup() {
    const { user } = useAuth0();
    return { user };
  },
  data() {
    return {
      // Source scans flattened into a uniform shape: every entry has scanId, name, transform,
      // trimRadius. Primary is included with its identity transform + the version's primary trim.
      sourceScans: [],
      sourcePrimaryScanId: null,
      newPrimaryScanId: null,
      keepScanIds: [],
      addScanIds: [],
      addableScans: [],
      versionName: '',
      versionDescription: '',
      setToCurrent: false,
      submitting: false,
      errorMessage: null,
    };
  },
  computed: {
    sourceVersionLabel() {
      const v = this.sourceVersion;
      return v ? `${v.versionName ?? 'Untitled'} (v${v.versionNumber})` : 'Loading…';
    },
    canSubmit() {
      // The primary must be a carry-over scan so we can rebase the other carry-overs against it.
      // (Newly added scans have no measured relationship to the source composition.)
      return !!(this.versionName && this.newPrimaryScanId && this.keepScanIds.length);
    },
  },
  async mounted() {
    await this.initFromSource();
  },
  methods: {
    async initFromSource() {
      const v = this.sourceVersion;
      if (!v) return;

      // Build a uniform [{scanId, name, transform, trimRadius}] list from the source version.
      // Prefer the API's combined `scans` projection (primary at identity + supplementaries) and
      // fall back to assembling from associatedScan + supplementaryScans for older responses.
      const allScans = await ModelVersionService.getScansByAssetId(this.assetId);
      const nameById = new Map(allScans.map(s => [s.id, s.name]));

      const combined = (v.scans && v.scans.length)
        ? v.scans
        : [
            { scanId: v.associatedScan, transform: identityTransform(), trimRadius: 0 },
            ...(v.supplementaryScans ?? []),
          ];

      this.sourceScans = combined
        .filter(s => !!s.scanId)
        .map(s => ({
          scanId: s.scanId,
          name: nameById.get(s.scanId) ?? 'Unknown scan',
          transform: s.transform ?? identityTransform(),
          trimRadius: Number(s.trimRadius) || 0,
        }));

      this.sourcePrimaryScanId = v.associatedScan ?? this.sourceScans[0]?.scanId ?? null;
      this.newPrimaryScanId = this.sourcePrimaryScanId;
      this.keepScanIds = this.sourceScans.map(s => s.scanId);
      this.addableScans = allScans
        .filter(s => s.status === SCAN_PROCESSING_SUCCESS && !this.keepScanIds.includes(s.id));
      this.versionName = v.versionName ? `${v.versionName} (copy)` : '';
      this.versionDescription = v.description ?? '';
    },

    toggleKeep(scanId) {
      const i = this.keepScanIds.indexOf(scanId);
      if (i >= 0) {
        this.keepScanIds.splice(i, 1);
        // Dropping the current new-primary candidate → fall back to the first still-kept scan.
        if (scanId === this.newPrimaryScanId) {
          this.newPrimaryScanId = this.keepScanIds[0] ?? null;
        }
      } else {
        this.keepScanIds.push(scanId);
      }
    },

    async onCreate() {
      this.errorMessage = null;
      this.submitting = true;
      try {
        // Rebase the kept source scans onto whichever carry-over scan the user picked as primary.
        const rebased = rebaseCompositionOnNewPrimary({
          sourceScans: this.sourceScans,
          newPrimaryScanId: this.newPrimaryScanId,
          keepScanIds: this.keepScanIds,
        });

        // Newly added scans always come in as supplementaries at identity — the user positions
        // them against the rebased composition in the alignment editor afterwards.
        const newSupplements = this.addScanIds.map(scanId => ({
          scanId,
          transform: identityTransform(),
          trimRadius: 0,
        }));

        const supplementaryScans = [...rebased.supplementaryScans, ...newSupplements];

        const createdBy = this.user?.value?.name ?? this.user?.value?.email ?? null;
        const v = this.sourceVersion ?? {};
        const viewpoint = {
          x: v.modelViewPointX ?? 0,
          y: v.modelViewPointY ?? 0,
          z: v.modelViewPointZ ?? 0,
          rotationX: v.modelViewPointRotationX ?? 0,
          rotationY: v.modelViewPointRotationY ?? 0,
          rotationZ: v.modelViewPointRotationZ ?? 0,
        };

        const payload = new CreateModelVersionModel(
          this.assetId,
          this.versionName,
          this.versionDescription,
          rebased.primaryScanId,
          supplementaryScans,
          this.setToCurrent,
          createdBy,
          rebased.primaryScanTrimRadius,
          viewpoint,
        );

        const result = await ModelVersionService.createNewModelVersionRecord(payload);
        this.$emit('created', result);
      } catch (e) {
        this.errorMessage = e?.message ?? 'Failed to create version';
        if (process.env.NODE_ENV === 'development') {
          console.error('DuplicateVersionDialog: create failed', e);
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.duplicate-form {
  padding: 4px 6px 8px;
  color: #cfd8dc;
}

.form-header {
  margin-bottom: 16px;
}

.form-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  margin: 2px 0 8px;
}

.form-hint {
  font-size: 0.85rem;
  color: #8aa0aa;
  margin: 0;
  line-height: 1.5;
}

.picker {
  margin-top: 14px;
  padding: 14px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
}

.section-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
  margin-bottom: 4px;
}

.section-hint {
  font-size: 0.8rem;
  color: #8aa0aa;
  margin: 0 0 10px;
  line-height: 1.45;
}

.scan-rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.scan-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid transparent;
  border-radius: 8px;
}

.scan-row.is-primary {
  border-color: rgba(100, 255, 218, 0.35);
  background: rgba(100, 255, 218, 0.06);
}

.scan-row.is-dropped {
  opacity: 0.5;
}

.scan-radio {
  width: 18px;
}

.scan-radio input { accent-color: var(--duallo-accent); cursor: pointer; }

.scan-body {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.scan-name {
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scan-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 7px;
  border-radius: 999px;
  flex: 0 0 auto;
}

.scan-badge.was {
  background: rgba(255, 255, 255, 0.04);
  color: #8aa0aa;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.scan-badge.new {
  background: rgba(100, 255, 218, 0.12);
  color: var(--duallo-accent);
  border: 1px solid rgba(100, 255, 218, 0.3);
}

.scan-meta {
  font-size: 0.72rem;
  color: var(--duallo-accent-muted);
  font-variant-numeric: tabular-nums;
  flex: 0 0 auto;
  margin-left: auto;
}

.scan-toggle {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--duallo-accent-muted);
  border-radius: 8px;
  height: 28px;
  width: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}

.scan-toggle:hover {
  background: rgba(100, 255, 218, 0.08);
}

.scan-row.is-dropped .scan-toggle {
  color: #6b7a82;
}

.add-select { margin-top: 4px; }

.empty-note {
  font-size: 0.8rem;
  color: #8aa0aa;
  font-style: italic;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
